import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useSelectBase } from './useSelectBase'
import { useState, useMemo, useCallback, useEffect, useRef } from 'react'

function findKeyDeep(obj, key) {
  if (!obj || typeof obj !== 'object') return undefined

  // encontrou na raiz
  if (key in obj && obj[key] != null) {
    return obj[key]
  }

  // procura nos filhos
  for (const value of Object.values(obj)) {
    if (typeof value === 'object') {
      const found = findKeyDeep(value, key)

      if (found != null) {
        return found
      }
    }
  }

  return undefined
}

const useRemoteSelectOptions = ({
  queryKey,
  entityPath, // Caminho da API (ex: entities.customers.get)
  baseServiceGet, // Instância do hook de serviço
  searchColumn = 'name',
  extraFilters = {}, // Filtros específicos da tela
  defaultSort = { column: 'id', order: 'asc' }, // NOVO: Sort padrão
  fetcher,
  mapToOption,
  fieldId,
  formData,
  setFormData,
  isMulti = false,
  enabled = true,
  immediateFetch = false,
  initialPage = 1,
  perPage = 25,
  enablePagination = true,
  searchDebounceMs = 300,
  blockedIds = [], // IDs bloqueados para exclusão das opções, usado para evitar seleção de itens indesejados
  refetchWhen, // Função opcional que retorna um valor para determinar quando refetch deve ser chamado
  staleTime = 0,
  refetchOnMount = true,
  refetchOnWindowFocus = false,
  initialId = null, // Novo: ID para carregar um item específico no início
  fetcherById = null, // Novo: Função para buscar um único item pelo ID
  columnDataBase = 'name', // Campo base para buscas de termo
  defaultValue,
  withoutDeletedAt = false,

  // * ItemListSection
  action = '',
  dependencies = [], // Array que sera usado como dependência para remover itens das opções, apenas quando for pra adicioanar algo no ItemListSection
  key_dependency = '', // Campo usado como chave para identificar e bloquear o ID dentro de "dependencies". Útil em casos como "contract_item_id", que possui IDs versionados e exige o uso do "root_item_id".
}) => {
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [wasOpened, setWasOpened] = useState(false)
  const [initialOption, setInitialOption] = useState(null)
  const [internalBlockedIds, setInternalBlockedIds] = useState(blockedIds)
  const hasFetchedInitial = useRef(false)
  const previousInitialId = useRef(initialId)

  // Remove a opção carregada por ID quando esse ID deixa de ser inicial.
  // Sem isso, ela continuaria nas opções mesmo após o filtro mudar.
  useEffect(() => {
    if (previousInitialId.current !== initialId) {
      previousInitialId.current = initialId
      hasFetchedInitial.current = false
      setInitialOption(null)
    }
  }, [initialId])

  useEffect(() => {
    if (!dependencies?.length) {
      setInternalBlockedIds(blockedIds)
      return
    }

    const newFieldId =
      fieldId.match(/\[(.*?)\]/)?.[1] || fieldId.split('.')?.[1]

    const usedIds = dependencies
      .map((item) => {
        return findKeyDeep(item, key_dependency) ?? item[newFieldId]?.value
      })
      .filter(Boolean)

    setInternalBlockedIds(usedIds)
  }, [dependencies, blockedIds, fieldId, key_dependency])

  // Efeito para carregar o item inicial
  useEffect(() => {
    const fetchInitial = async () => {
      if (
        initialId &&
        fetcherById &&
        !initialOption &&
        !hasFetchedInitial.current
      ) {
        hasFetchedInitial.current = true
        try {
          const result = await fetcherById(initialId)
          if (result) {
            setInitialOption(result)
          }
        } catch (error) {
          console.error('Erro ao carregar item inicial:', error)
          hasFetchedInitial.current = false
        }
      }
    }
    fetchInitial()
  }, [fetcherById, initialId, initialOption])

  // Debounce para busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, searchDebounceMs)

    return () => clearTimeout(timer)
  }, [searchTerm, searchDebounceMs])

  const isQueryEnabled = Boolean(enabled) && (wasOpened || immediateFetch)

  // FETCHER PADRÃO INTERNO - CORRIGIDO
  const defaultFetcher = useCallback(
    async ({ page, perPage, search }) => {
      // 1. Monta o filtro de busca baseado no columnDataBase
      let searchFilter = {}

      if (search) {
        // Se columnDataBase for um array, usa $or com $contains
        if (Array.isArray(columnDataBase)) {
          searchFilter = {
            $or: columnDataBase.map((col) => ({
              [col]: { $contains: search },
            })),
          }
        }
        // Se columnDataBase for string, usa aquela coluna
        else if (columnDataBase) {
          searchFilter = { [columnDataBase]: { $contains: search } }
        }
        // Fallback: usa searchColumn (retrocompatibilidade)
        else {
          searchFilter = { [searchColumn]: search }
        }
      }

      // 2. Monta os filtros restantes
      const restFilters = {
        ...(!withoutDeletedAt && { deleted_at: { $eq: 'null' } }),
        ...extraFilters,
      }

      // 3. Monta os parâmetros finais
      const params = {
        filters: {
          ...restFilters,
          ...searchFilter, // Mescla os filtros
        },
        page,
        perPage,
        sort: defaultSort,
      }

      // 4. Chama a API
      const response = await baseServiceGet(entityPath, params)
      return response.result
    },
    [
      withoutDeletedAt,
      extraFilters,
      defaultSort,
      baseServiceGet,
      entityPath,
      columnDataBase,
      searchColumn,
    ]
  )

  const infiniteQueryResult = useInfiniteQuery({
    queryKey: [...queryKey, debouncedSearchTerm, extraFilters],
    queryFn: async ({ pageParam }) => {
      const executeFetch = fetcher || defaultFetcher
      return await executeFetch({
        page: pageParam,
        perPage,
        search: debouncedSearchTerm,
      })
    },
    initialPageParam: initialPage,
    getNextPageParam: (lastPage) => {
      if (lastPage?.current_page && lastPage?.last_page) {
        if (lastPage.current_page < lastPage.last_page) {
          return lastPage.current_page + 1
        }
      }
      return undefined
    },
    enabled: isQueryEnabled,
    staleTime: staleTime,
    refetchOnMount: refetchOnMount,
    refetchOnWindowFocus: refetchOnWindowFocus,
    gcTime: 10 * 60 * 1000,
  })

  // Merge do item inicial com os dados da lista infinita
  const rawData = useMemo(() => {
    let list = []
    if (infiniteQueryResult.data?.pages) {
      list = infiniteQueryResult.data.pages.flatMap((page) => {
        if (Array.isArray(page)) return page
        if (page?.data) return page.data
        if (page?.result?.data) return page.result.data
        if (page?.result && !page.result.data) return page.result
        return []
      })
    }

    if (initialOption && !list.find((item) => item.id === initialOption.id)) {
      return [initialOption, ...list]
    }
    return list
  }, [infiniteQueryResult.data, initialOption])

  const filteredRawData = useMemo(() => {
    if (!Array.isArray(rawData) || !internalBlockedIds.length) {
      return rawData
    }

    return rawData.filter((item) => {
      const itemKey = item[key_dependency] ?? item.root_item_id ?? item.id

      return !internalBlockedIds.some(
        (blockedId) => String(blockedId) === String(itemKey)
      )
    })
  }, [rawData, internalBlockedIds, key_dependency])

  const formDataRef = useRef(formData)
  formDataRef.current = formData

  const refetch = useCallback((options) => {
    const cleanedForm = options?.cleanedForm ?? false
    queryClient.invalidateQueries({ queryKey, exact: false })
    if (cleanedForm) {
      const field = fieldId.split('.')
      formDataRef.current[field[0]][field[1]] = isMulti
        ? [{ value: null, label: '' }]
        : { value: null, label: '' }
    }
  }, [fieldId, isMulti, queryClient, queryKey]
  )

  const handleMenuScrollToBottom = useCallback(() => {
    if (
      infiniteQueryResult.hasNextPage &&
      !infiniteQueryResult.isFetchingNextPage
    ) {
      infiniteQueryResult.fetchNextPage()
    }
  }, [infiniteQueryResult])

  const handleSearch = useCallback((term) => {
    setSearchTerm(term)
  }, [])

  const activateQuery = useCallback(() => {
    setWasOpened(true)
  }, [])

  const { options, selectedValue, reset } = useSelectBase({
    fieldId,
    formData,
    setFormData,
    rawOptions: filteredRawData,
    mapToOption,
    isMulti,
    defaultValue,
  })

  useEffect(() => {
    if (typeof refetchWhen === 'function') {
      const value = refetchWhen()
      if (value) {
        refetch()
      }
    }
  }, [refetchWhen, refetch])

  return {
    options,
    selectedValue,
    reset,
    isLoading: infiniteQueryResult.isLoading,
    isFetching: infiniteQueryResult.isFetching,
    error: infiniteQueryResult.error,
    rawData,
    refetch,
    hasNextPage: infiniteQueryResult.hasNextPage,
    isFetchingNextPage: infiniteQueryResult.isFetchingNextPage,
    handleMenuScrollToBottom: enablePagination
      ? handleMenuScrollToBottom
      : () => {},
    handleSearch: enablePagination ? handleSearch : () => {},
    activateQuery,
    setBlockedIds: setInternalBlockedIds,
  }
}

export default useRemoteSelectOptions
